import React from "react";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { useTheme } from "@/context/ThemeContext";
import { Sun, Moon, LogOut, ChevronDown, Menu } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useNavigate } from "react-router-dom";

export default function TopBar({ title, subtitle, right, onOpenMobile }) {
  const { user, logout } = useAuth();
  const { lang, setLang, t } = useLang();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();
  const initials = (user?.name || user?.email || "U").slice(0, 2).toUpperCase();

  return (
    <header data-testid="topbar" className="sticky top-0 z-30 glass border-b border-zinc-800">
      <div className="flex items-center justify-between gap-3 px-4 sm:px-6 py-3 sm:py-4">
        <div className="flex items-center gap-3 min-w-0">
          {onOpenMobile && (
            <button data-testid="mobile-menu-btn" onClick={onOpenMobile} className="btn-scale lg:hidden h-9 w-9 grid place-items-center rounded-md border border-zinc-800 text-zinc-300 hover:bg-zinc-900">
              <Menu className="w-4 h-4" />
            </button>
          )}
          <div className="min-w-0">
            <h1 className="font-display text-lg sm:text-xl font-semibold tracking-tight text-zinc-100 truncate" data-testid="topbar-title">{title}</h1>
            {subtitle && <p className="text-xs sm:text-sm text-zinc-500 mt-0.5 truncate hidden sm:block" data-testid="topbar-subtitle">{subtitle}</p>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="hidden sm:contents">{right}</div>
          <button data-testid="lang-toggle" onClick={() => setLang(lang === "es" ? "en" : "es")} className="btn-scale h-9 px-2 sm:px-3 rounded-md border border-zinc-800 text-[10px] sm:text-xs uppercase tracking-widest text-zinc-300 hover:bg-zinc-900">
            {lang.toUpperCase()}
          </button>
          <button data-testid="theme-toggle" onClick={toggle} className="btn-scale h-9 w-9 grid place-items-center rounded-md border border-zinc-800 text-zinc-300 hover:bg-zinc-900" aria-label="Toggle theme">
            {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="user-menu" className="btn-scale flex items-center gap-2 h-9 pl-1 pr-2 rounded-md border border-zinc-800 hover:bg-zinc-900">
                <Avatar className="h-7 w-7">
                  <AvatarImage src={user?.picture || undefined} alt={user?.name} />
                  <AvatarFallback className="text-[10px] bg-zinc-800 text-zinc-100">{initials}</AvatarFallback>
                </Avatar>
                <span className="text-xs text-zinc-300 max-w-[120px] truncate hidden sm:block">{user?.name || user?.email}</span>
                <ChevronDown className="w-3 h-3 text-zinc-500" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56 bg-zinc-900 border-zinc-800">
              <div className="px-2 py-1.5 text-xs">
                <div className="text-zinc-100">{user?.name}</div>
                <div className="text-zinc-500">{user?.email}</div>
                <div className="mt-1 text-[10px] uppercase tracking-widest text-blue-400">{user?.role}</div>
              </div>
              <DropdownMenuSeparator className="bg-zinc-800" />
              <DropdownMenuItem data-testid="menu-profile" onClick={() => navigate("/app/profile")} className="text-zinc-300 focus:bg-zinc-800 focus:text-zinc-100">
                {t("sidebar.profile")}
              </DropdownMenuItem>
              <DropdownMenuItem data-testid="menu-settings" onClick={() => navigate("/app/settings")} className="text-zinc-300 focus:bg-zinc-800 focus:text-zinc-100">
                {t("sidebar.settings")}
              </DropdownMenuItem>
              <DropdownMenuSeparator className="bg-zinc-800" />
              <DropdownMenuItem data-testid="menu-logout" onClick={async () => { await logout(); navigate("/login"); }} className="text-red-400 focus:bg-zinc-800 focus:text-red-300">
                <LogOut className="w-3.5 h-3.5 mr-2" /> {t("common.logout")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      {right && <div className="sm:hidden flex justify-end px-4 pb-3 -mt-1">{right}</div>}
    </header>
  );
}
